// D-043 — the deterministic seed the art generators are built on.
//
// FNV-1a over the UTF-16 code units of the id. No crypto, no `Math.random`, no clock: the same
// string must produce byte-identical output on every machine, forever, because a generated app
// icon is cached and compared (§9.6) and a wallpaper is persisted by name (§14).

export function hashSeed(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    // 32-bit FNV prime multiply, kept in range without BigInt.
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/**
 * A small deterministic sequence derived from one seed. Each `next()` advances an xorshift32
 * state, so a generator can draw several independent-looking choices from a single id.
 */
export function createDraw(seed: number): { next(): number; pick<T>(items: readonly T[]): T; range(min: number, max: number): number } {
  let state = seed === 0 ? 0x9e3779b9 : seed;

  const next = (): number => {
    state ^= state << 13;
    state >>>= 0;
    state ^= state >>> 17;
    state ^= state << 5;
    state >>>= 0;
    return state;
  };

  return {
    next,
    pick<T>(items: readonly T[]): T {
      const item = items[next() % items.length];
      if (item === undefined) throw new Error('createDraw.pick: empty list');
      return item;
    },
    range(min, max) {
      if (max <= min) return min;
      return min + (next() % (max - min));
    },
  };
}
